'use client'

import * as React from 'react'
import { FirebaseError } from 'firebase/app'
import { Mail, ArrowLeft } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type AuthMode = 'sign-in' | 'sign-up' | 'reset-password'

interface AuthDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Tab to show when the dialog opens (defaults to sign-in). */
  initialMode?: 'sign-in' | 'sign-up'
}

const firebaseErrorMessages: Record<string, string> = {
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/user-not-found': 'Invalid email or password.',
  'auth/wrong-password': 'Invalid email or password.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/popup-closed-by-user': '',
  'auth/cancelled-popup-request': '',
}

function getErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    const mapped = firebaseErrorMessages[error.code]
    if (mapped !== undefined) return mapped
  }
  return 'Something went wrong. Please try again.'
}

export function AuthDialog({ open, onOpenChange, initialMode }: AuthDialogProps) {
  const [mode, setMode] = React.useState<AuthMode>('sign-in')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [resetSent, setResetSent] = React.useState(false)

  // Jump to the requested tab on open (e.g. the welcome gate's "Create your
  // account" opens straight into sign-up).
  React.useEffect(() => {
    if (open && initialMode) setMode(initialMode)
  }, [open, initialMode])

  // Reset state when dialog opens/closes or mode changes
  React.useEffect(() => {
    if (!open) {
      // Delay reset so closing animation completes
      const timer = setTimeout(() => {
        setMode('sign-in')
        setEmail('')
        setPassword('')
        setName('')
        setError('')
        setLoading(false)
        setResetSent(false)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [open])

  React.useEffect(() => {
    setError('')
    setPassword('')
    setResetSent(false)
  }, [mode])

  const handleGoogleSignIn = async () => {
    setError('')
    setLoading(true)
    try {
      const { signInWithGoogle } = await import('@/lib/firebase')
      await signInWithGoogle()
      onOpenChange(false)
    } catch (err) {
      const msg = getErrorMessage(err)
      if (msg) setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'sign-in') {
        const { signInWithEmail } = await import('@/lib/firebase')
        await signInWithEmail(email, password)
        onOpenChange(false)
      } else if (mode === 'sign-up') {
        const { signUpWithEmail } = await import('@/lib/firebase')
        await signUpWithEmail(email, password, name.trim())
        onOpenChange(false)
      } else if (mode === 'reset-password') {
        const { resetPassword } = await import('@/lib/firebase')
        await resetPassword(email)
        setResetSent(true)
      }
    } catch (err) {
      const msg = getErrorMessage(err)
      if (msg) setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {mode === 'sign-in' && 'Sign in'}
            {mode === 'sign-up' && 'Create account'}
            {mode === 'reset-password' && 'Reset password'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'sign-in' && 'Sign in to save searches, track listings, and more.'}
            {mode === 'sign-up' && 'Create an account to get started.'}
            {mode === 'reset-password' && "Enter your email and we'll send you a reset link."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 py-4 sm:px-6 space-y-4">
          {/* Mode toggle (sign-in / sign-up) */}
          {mode !== 'reset-password' && (
            <div className="flex rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => setMode('sign-in')}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  mode === 'sign-in'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setMode('sign-up')}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  mode === 'sign-up'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Sign up
              </button>
            </div>
          )}

          {/* Google sign-in */}
          {mode !== 'reset-password' && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>
            </>
          )}

          {/* Reset password success */}
          {mode === 'reset-password' && resetSent ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-primary/10 p-4 text-center">
                <Mail className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">Check your email</p>
                <p className="text-xs text-muted-foreground mt-1">
                  We sent a password reset link to {email}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setMode('sign-in')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to sign in
              </Button>
            </div>
          ) : (
            /* Email/password form */
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              {mode === 'sign-up' && (
                <div className="space-y-1.5">
                  <label htmlFor="auth-name" className="text-sm font-medium text-foreground">
                    Name
                  </label>
                  <Input
                    id="auth-name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="auth-email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  id="auth-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              {mode !== 'reset-password' && (
                <div className="space-y-1.5">
                  <label htmlFor="auth-password" className="text-sm font-medium text-foreground">
                    Password
                  </label>
                  <Input
                    id="auth-password"
                    type="password"
                    placeholder={mode === 'sign-up' ? 'At least 6 characters' : 'Your password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
                  />
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading
                  ? 'Please wait...'
                  : mode === 'sign-in'
                    ? 'Sign in'
                    : mode === 'sign-up'
                      ? 'Create account'
                      : 'Send reset link'}
              </Button>

              {mode === 'sign-in' && (
                <button
                  type="button"
                  onClick={() => setMode('reset-password')}
                  className="block w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Forgot password?
                </button>
              )}

              {mode === 'reset-password' && (
                <button
                  type="button"
                  onClick={() => setMode('sign-in')}
                  className="flex items-center justify-center gap-1 w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to sign in
                </button>
              )}
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
